//types.ts
// ----------------------------------------------------
// ROUTING / VIEWS
// ----------------------------------------------------

export enum View {
  Home = 'Home',
  Map = 'Map',
  BigBlueGlobe = 'BigBlueGlobe',
  Devices = 'Gadgets',
  Community = 'Community',
  Crew = 'Crew',
  CrewSettings = 'CrewSettings',
  Bazaar = 'Bazaar',
  Events = 'Events',
  TackleBox = 'tackleBox',
  Profile = 'Profile',
  Account = 'Account',
  RoddoRadio = 'RoddoRadio',
  PublicProfile = 'PublicProfile',
  MyPage = 'MyGear',
  LogCatch = 'LogCatch',
  Admin = 'Super Secret Panel',
  EditProfile = 'Edit My Info',
  SellerDashboard = 'Seller Hub',
  Challenges = 'Challenges',
  FishIQ = 'FishIQ',
  Notifications = 'Notifications',
  NotificationSettings = 'NotificationSettings',
  LicenseDetail = 'LicenseDetail',
  Achievements = 'Achievements',
  CatchHistory = 'CatchHistory',
  // Stripe checkout result views
  CheckoutSuccess = 'CheckoutSuccess',
  CheckoutCancel = 'CheckoutCancel',
  PurchaseHistory = 'PurchaseHistory',
  Receipt = 'Receipt',
  Support = 'Support',
  Legal = 'Legal',
  TermsOfService = 'TermsOfService',
  PrivacyPolicy = 'PrivacyPolicy',
}

// ----------------------------------------------------
// NOTIFICATIONS & TOASTS
// ----------------------------------------------------

export enum NotificationType {
  NEW_LIKE = 'NEW_LIKE',
  NEW_COMMENT = 'NEW_COMMENT',
  NEW_REPLY = 'NEW_REPLY',
  NEW_FOLLOW = 'NEW_FOLLOW',
  ADMIN_ANNOUNCEMENT = 'ADMIN_ANNOUNCEMENT',
  PRICE_DROP = 'PRICE_DROP',
  FISHING_ALERT = 'FISHING_ALERT',
  COMMUNITY_EVENT = 'COMMUNITY_EVENT',
  LICENSE_EXPIRING = 'LICENSE_EXPIRING',
  RESTRICTED_AREA = 'RESTRICTED_AREA',
  CHALLENGE_COMPLETE = 'CHALLENGE_COMPLETE',
  LEVEL_UP = 'LEVEL_UP',
  NEW_OFFER = 'NEW_OFFER',
  OFFER_ACCEPTED = 'OFFER_ACCEPTED',
  OFFER_DECLINED = 'OFFER_DECLINED',
  OFFER_COUNTERED = 'OFFER_COUNTERED',
  TOURNAMENT_ANNOUNCEMENT = 'TOURNAMENT_ANNOUNCEMENT',

  // Marketplace / Stripe
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  ESCROW_RELEASED = 'ESCROW_RELEASED',
  NEW_MENTION = 'NEW_MENTION',
}

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

export interface NotificationLink {
  view: View;
  targetId: string;
  subTargetId?: string; // commentId, offerId, etc
}

export interface NotificationActor {
  uid?: string;
  name?: string;
  avatar?: string;
}

export interface NotificationEntity {
  postId?: string;
  commentId?: string;
  parentCommentId?: string;
  itemId?: string;
  offerId?: string;
  txId?: string;
  catchId?: string;
  userId?: string;
  tournamentId?: string;
  licenseId?: string;
  reaction?: CommunityReactionKey | string;
}

export interface Notification {
  id: string;
  type: NotificationType;

  // display
  title?: string; // "New like"
  body?: string; // "Scott liked your catch post"
  message?: string; // legacy (still supported)

  // routing
  link?: NotificationLink;

  // metadata
  actor?: NotificationActor;
  entity?: NotificationEntity;

  // times
  timestamp: number; // ms
  read: boolean;
}

export interface NotificationSettings {
  enabled?: boolean;
  newLikes: boolean;
  newComments: boolean;
  priceDrops: boolean;
  /** Crew/private map presence. Canonical key used by Crew Deck. */
  shareCrewMapPresence?: boolean;
  /** Backward-compatible alias for older settings UI wiring. */
  crewMapPresence?: boolean;
  /** Backward-compatible alias for older settings UI wiring. */
  crewMapPresenceEnabled?: boolean;
  /** Controls whether RoddoInsightCard auto-opens after saving a catch. Defaults to true. */
  showPostCatchInsight?: boolean;
}

export interface AlertSettings {
  enabled: boolean;
  targetSpecies: string[];
  scoreThreshold: number;
}

export interface ForecastSettings {
  duration: 3 | 5;
}

// ----------------------------------------------------
// CORE DOMAIN MODELS – CATCHES / LOGGING
// ----------------------------------------------------


export type FishIQObservationType =
  | 'catch'
  | 'missed_bite'
  | 'follow'
  | 'surface_activity'
  | 'baitfish_seen'
  | 'birds_working'
  | 'schooling_seen'
  | 'no_catch_session';

export type FishIQActivityClue =
  | 'baitfish_seen'
  | 'birds_working'
  | 'surface_boils'
  | 'schooling_seen'
  | 'fish_followed_lure'
  | 'short_strikes'
  | 'missed_bites'
  | 'no_visible_activity';

export type FishIQPressureTrend = 'rising' | 'falling' | 'steady' | 'unknown' | string;

export interface FishIQWeatherSnapshot {
  conditionText?: string | null;
  temp?: number | null;
  tempUnit?: 'Celsius' | 'Fahrenheit' | string | null;
  windText?: string | null;
  windSpeed?: number | null;
  windDirection?: string | null;
  pressureTrend?: FishIQPressureTrend | null;
  cloudCover?: string | null;
  moonPhase?: string | null;
}

export interface FishIQWaterSnapshot {
  waterCondition?: string | null;
  waterClarity?: 'clear' | 'stained' | 'muddy' | 'unknown' | string;
  waterLevel?: 'low' | 'normal' | 'high' | 'unknown' | string;
  tideStage?: string | null;
  currentFlow?: string | null;
  waterTemp?: number | null;
  waterTempUnit?: 'Celsius' | 'Fahrenheit' | string | null;
}

export interface LogEntry {
  id: string; // Firestore document ID
  userId: string;
  species: string;
  weight: string;
  date: string;
  time: string;
  location: string;
  image: string;
  timestamp: number;
  lureOrBait?: string;
  lureOrBaitType?: 'Lure' | 'Bait';
  rod?: string;
  reel?: string;
  technique?: string;
  weather?: string;

  /**
   * FishIQ stewardship consent for this individual log.
   * false/private-only means the log can stay in the user's diary without entering shared rollups.
   */
  fishiqContributionConsent?: boolean;
  fishiqContributionConsentVersion?: string;
  fishiqContributionConsentAt?: number | null;
  fishiqPrivacyMode?: 'private_only' | 'generalized_aggregate' | string;
  fishiqPrivacySummary?: string;

  // Research-safe optional context for FishIQ Pilot / grant work.
  // These are lightweight observation fields; exact spots stay private in user-owned catchDiary docs.
  fishiqPilotPartnerCode?: string | null;
  fishiqPilotCohortCode?: string | null;
  researchObservationVersion?: string;
  observationType?: FishIQObservationType;
  activityClues?: FishIQActivityClue[];
  biteOutcome?: 'landed' | 'missed' | 'follow' | 'observed_activity' | 'no_catch' | 'unknown' | string;
  missedBiteCount?: number | null;
  followCount?: number | null;
  weatherSnapshot?: FishIQWeatherSnapshot | null;
  waterSnapshot?: FishIQWaterSnapshot | null;
  waterClarity?: 'clear' | 'stained' | 'muddy' | 'unknown' | string;
  waterLevel?: 'low' | 'normal' | 'high' | 'unknown' | string;
  catchDisposition?: 'released' | 'harvested' | 'unknown' | string;
  effortMinutes?: number | null;
  anglerCount?: number | null;
  generalizedRegionKey?: string | null;
  generalizationMethod?: string | null;
  generalizationPrecision?: number | null;
  [key: string]: any;
}

// ----------------------------------------------------
// FISHIQ SCAN
// ----------------------------------------------------

export interface FishScanResult {
  species: string;
  confidence: number;

  lengthInches?: number;
  lengthConfidence?: number;

  weightLbs?: number;
  weightConfidence?: number;

  notes?: string;
}

// ----------------------------------------------------
// LICENSES
// ----------------------------------------------------

export interface LicenseInfo {
  id: string;
  state: string;
  type: string;
  expiry: string;
  active: boolean;
  price: number;
}

/**
 * Persistent user license record stored in Firestore.
 * Dates are ISO strings so they can round-trip cleanly and
 * be used with `new Date(...)` on the client.
 */
export interface UserLicense {
  id: string;
  userId: string;
  licenseNumber: string;

  jurisdictionCountry?: string;
  jurisdictionState: string;

  state?: string;
  type?: string;

  waterType?: 'freshwater' | 'saltwater' | 'both';
  tier?: 'basic' | 'sport' | 'youth' | 'senior';

  purchasedAt?: string;
  startDate?: string;
  endDate?: string;

  status?: 'active' | 'expired' | 'revoked';

  expiryDate?: string | null;
  active?: boolean;
  notes?: string;

  photoUrl?: string;
  licensePhotoUrl?: string;
}

// ----------------------------------------------------
// Marketplace / Offers / Escrow / Shipping
// ----------------------------------------------------

export type MarketplaceItemStatus = 'active' | 'pending' | 'sold' | 'archived' | 'cancelled';

export type OfferStatus = 'pending' | 'accepted' | 'rejected' | 'withdrawn';

export interface Offer {
  id: string;

  itemDocId?: string;
  itemId?: string | number;

  buyerId: string;
  buyerName: string;
  buyerAvatar?: string | null;

  message?: string;
  note?: string;

  amountCents?: number;
  amount?: number;

  status: OfferStatus;
  timestamp: number;
  createdAt?: number;
  updatedAt?: number;

  kind?: 'inquiry' | 'offer' | 'counter';
}

export type ShippingStatus =
  | 'NONE'
  | 'LABEL_CREATED'
  | 'IN_TRANSIT'
  | 'DELIVERED'
  | 'RETURNED';

export type EscrowStatus = 'HELD' | 'RELEASED' | 'REFUNDED';

export interface MarketplaceItem {
  // Canonical Firestore document/listing id. Legacy numeric ids remain metadata only.
  id: string;
  docId?: string;

  legacyDocId?: string;
  legacyNumericId?: number;

  title: string;
  name?: string;

  description?: string;

  price?: number;
  priceCents: number;
  currency: string;

  condition?: string;
  quantity?: number;

  images?: string[];
  imageUrls?: string[];
  imagePaths?: string[];
  imageUrl?: string;

  sellerId: string;
  sellerName: string;
  sellerAvatar?: string;
  sellerUsername?: string;

  sellerStripeAccountId?: string | null;

  createdAt: number;
  status: MarketplaceItemStatus;
  fulfillmentMethods?: string[];
  checkoutMethods?: string[];
  locationLabel?: string;
  city?: string;
  region?: string;
  countryCode?: string;

  boostTokensSpent?: number;
  boostedUntil?: number;
  lastBoostedAt?: number;
  boostScore?: number;

  offers?: Record<string, Offer> | Offer[];

  shippingCarrier?: string | null;
  trackingNumber?: string | null;
  shippingStatus?: ShippingStatus;
  lastShippingUpdateAt?: number;
}

export interface MarketplaceTransaction {
  id: string;

  itemId: string;
  buyerId: string;
  sellerId: string;

  amount: number; // cents
  currency: string;

  status: 'succeeded' | 'refunded' | 'failed' | 'pending' | 'disputed';
  // Compatibility only until Stripe Connect payout architecture is enabled.
  escrowStatus?: EscrowStatus;

  createdAt: number;
  releasedAt?: number;

  stripePaymentIntentId?: string;
  stripeCheckoutSessionId?: string;

  shippingCarrier?: string | null;
  trackingNumber?: string | null;
  shippingStatus?: ShippingStatus;
  lastShippingUpdateAt?: number;

  receiptUrl?: string | null;
  itemTitle?: string | null;
  itemImageUrl?: string | null;
  updatedAt?: number;
  payoutStatus?: string | null;
}

export interface EscrowInfo {
  transactionId: string;
  escrowStatus: EscrowStatus;

  status?: 'succeeded' | 'refunded' | 'failed';
  amountCents?: number;
  receiptUrl?: string | null;
  shippingStatus?: ShippingStatus;
  createdAt?: number;
  releasedAt?: number;
}

// ---------------------------------------------
// GEAR / TACKLE BOX
// ---------------------------------------------

export type GearCategory =
  | 'rod'
  | 'reel'
  | 'lure'
  | 'bait'
  | 'line'
  | 'smart'
  | 'other';

export interface TackleBoxItem {
  id: string;
  name: string;
  category: GearCategory;
  brand?: string;
  model?: string;
  notes?: string;
  imageUrl?: string;
  value?: number;
  weightOz?: number;
  catches?: number;
  favorite?: boolean;
}

export interface TackleBoxSet {
  id: string;
  name: string;
  rodId: string | null;
  reelId: string | null;
  lureId: string | null;
  isActive: boolean;
}

export type EquippedGear = {
  rodId: string | null;
  reelId: string | null;
  lureId: string | null;
};

export type CatchLog = {
  rodId?: string | null;
  reelId?: string | null;
  lureId?: string | null;
  rodName?: string;
  reelName?: string;
  lureName?: string;
};

// ----------------------------------------------------
// USER PROFILE
// ----------------------------------------------------

export interface PublicProfileVisibility {
  showStats?: boolean;
  showAchievements?: boolean;
  showAbout?: boolean;
}

export type UserReportReason =
  | 'spam'
  | 'harassment'
  | 'impersonation'
  | 'inappropriate_content'
  | 'scam'
  | 'other';

export interface UserReport {
  id: string;
  reporterUid: string;
  targetUserId: string;
  postId?: string | null;
  reason: UserReportReason;
  details?: string;
  status: 'open' | 'reviewing' | 'resolved' | 'dismissed';
  createdAt: number;
  updatedAt?: number;
  reporterName?: string;
  targetUserName?: string;
}

export interface FishIQPilotEnrollment {
  enrolled: boolean;
  status?: 'enrolled' | 'paused' | 'withdrawn' | string;
  partnerCode?: string | null;
  cohortCode?: string | null;
  consentVersion?: string;
  enrolledAt?: number | null;
  pausedAt?: number | null;
  updatedAt?: number;
}


// ----------------------------------------------------
// AVATAR GEAR / COSMETICS
// ----------------------------------------------------

export type CosmeticRarity =
  | 'common'
  | 'uncommon'
  | 'rare'
  | 'epic'
  | 'legendary'
  | 'seasonal';

export type CosmeticSlot =
  | 'hat'
  | 'hair'
  | 'faceCover'
  | 'top'
  | 'outerwear'
  | 'pants'
  | 'socks'
  | 'footwear'
  | 'rodSkin'
  | 'reelSkin'
  | 'lureCharm'
  | 'profileFrame'
  | 'badgeA'
  | 'badgeB'
  | 'badgeC'
  | 'background'
  | 'title';

export type AvatarBase =
  | 'user_icon'
  // Legacy saved values only. New UI should not expose gendered base labels.
  | 'boy'
  | 'girl'
  | 'dad'
  | 'mom'
  | 'grandpa'
  | 'grandma';

export interface UserCosmeticLoadout {
  hat?: string | null;
  hair?: string | null;
  hairStyle?: string | null;
  hairColor?: string | null;
  faceCover?: string | null;
  top?: string | null;
  outerwear?: string | null;
  pants?: string | null;
  socks?: string | null;
  footwear?: string | null;
  rodSkin?: string | null;
  reelSkin?: string | null;
  lureCharm?: string | null;
  profileFrame?: string | null;
  badgeA?: string | null;
  badgeB?: string | null;
  badgeC?: string | null;
  background?: string | null;
  title?: string | null;
}

export interface OwnedCosmetic {
  id: string;
  cosmeticId: string;
  slot: CosmeticSlot;
  name?: string;
  rarity?: CosmeticRarity | string;
  grantSource?: 'starter' | 'purchase' | 'tournament' | 'admin' | string;
  equipped?: boolean;
  createdAt?: any;
  updatedAt?: any;
}


export interface FoundingMemberRewards {
  eligible: boolean;
  cohort: 'founding_5000' | string;
  memberNumber?: number | null;
  packageVersion: number;
  rewardsGrantId?: string;
  rewardsGrantedAt?: number;
  grantedBy?: string;
}

export interface UserEntitlements {
  marketplace?: {
    featuredListingCredits?: number;
    listingBumpCredits?: number;
    complimentaryListingMonths?: number;
  };
  tournaments?: {
    entryTokens?: number;
    earlyRegistrationHours?: number;
    foundersCupEligible?: boolean;
  };
}

export interface UserProfile {
  uid: string;
  displayName: string;
  name: string;
  username?: string;
  usernameLower?: string;
  handle?: string;
  handleLower?: string;
  email?: string;
  accountType?: 'guest' | 'registered';
  authProviders?: string[];
  guestCreatedAt?: number;
  accountUpgradedAt?: number;
  avatar: string;
  avatarUrl?: string;
  avatarURL?: string;
  photoURL?: string;
  photoUrl?: string;
  profilePhotoUrl?: string;
  imageUrl?: string;
  avatarUpdatedAt?: number;
  joinDate: string;
  bio: string;
  isAdmin?: boolean;
  isSeller?: boolean;
  isPro?: boolean;
  reputation?: ReputationSummary;
  preferredLanguage?: 'en' | 'es' | 'ko' | string;
  language?: 'en' | 'es' | 'ko' | string;
  locale?: string;
  appLanguage?: string;
  fishiqContributionEnabled?: boolean;
  fishiqPilotEnrolled?: boolean;
  fishiqPilotPartnerCode?: string | null;
  fishiqPilotCohortCode?: string | null;
  fishiqPilotEnrollment?: FishIQPilotEnrollment;
  legal?: {
    consents?: {
      fishiqContribution?: {
        enabled?: boolean;
        version?: string;
        acceptedAt?: number | null;
        declinedAt?: number | null;
        updatedAt?: number;
      };
      [key: string]: any;
    };
    [key: string]: any;
  };
  memberNumber?: number;
  earlyPro?: boolean;
  foundingMember?: FoundingMemberRewards;
  entitlements?: UserEntitlements;
  shells?: number;
  lifetimeShells?: number;
  avatarBase?: AvatarBase;
  avatarBaseUpdatedAt?: number;
  cosmeticLoadout?: UserCosmeticLoadout;
  cosmeticLoadoutUpdatedAt?: number;

  accessStatus?: 'active' | 'waitlisted' | 'blocked';
  waitlistNumber?: number;
  invitedByCode?: string | null;

  memberNumberAssignedAt?: number;
  waitlistedAt?: number;
  accessGrantedAt?: number;
  accessApprovedAt?: number;
  accessApprovedBy?: string;

  createdAt?: number;

  unreadNotificationCount?: number;
  followingCount?: number;
  activeMarketplaceCount?: number;
  pendingOfferCount?: number;

  stats: {
    catches: number;
    species: number;
    spots: number;
    [key: string]: any;
  };

  achievements: { name: string; icon: string }[];
  publicProfileVisibility?: PublicProfileVisibility;

  log: LogEntry[];

  tackleBox?: TackleBoxItem[];
  savedSpots?: SavedSpotProfileRecord[];
  tackleBoxSets?: TackleBoxSet[];
  equippedGear?: {
    rodId?: string;
    reelId?: string;
    lureOrBaitId?: string;
    otherId?: string;
    useTackleBoxSets?: boolean;
  };

  notificationSettings?: NotificationSettings;
  alertSettings?: AlertSettings;

  xp?: number; 
  points: number;
  level: number;
  completedChallenges: string[];
  onboardingCompleted: boolean;
  following: string[];

  forecastSettings?: ForecastSettings;
}

export type ReputationTrackKey =
  | 'species_mastery'
  | 'explorer'
  | 'community_contributor'
  | 'bazaar_trader'
  | 'tournament_competitor';

export type ReputationTier =
  | 'none'
  | 'bronze'
  | 'silver'
  | 'gold'
  | 'platinum'
  | 'mythic';

export interface ReputationTrackState {
  key: ReputationTrackKey;
  value: number;
  tier: ReputationTier;
  nextTier: ReputationTier | null;
  nextThreshold: number | null;
  progressWithinTierPct: number;
}

export interface ReputationSummary {
  schemaVersion: number;
  tracks: Record<ReputationTrackKey, ReputationTrackState>;
  featuredBadgeKeys: string[];
  primaryBadgeKey: string | null;
  updatedAt?: number;
}
// ----------------------------------------------------
// COMMUNITY
// ----------------------------------------------------

export type CommunityReactionKey = 'like' | 'love' | 'care' | 'laugh' | 'sad' | 'angry';

export interface Comment {
  id: string;
  authorId: string;
  authorName?: string;
  authorUsername?: string;
  authorAvatar?: string;
    authorReputation?: {
    primaryBadgeKey?: string | null;
    featuredBadgeKeys?: string[];
    reputation?: ReputationSummary;
  };

  text: string;
  timestamp: number;

  postId?: string;
  createdAt?: number;

  parentCommentId?: string | null;
  replyToAuthorId?: string | null;

  likeCount?: number;
  replyCount?: number;
  likedBy?: string[];
  reaction?: CommunityReactionKey | string;
  status?: 'visible' | 'pending_review' | 'hidden';
  mentions?: CommunityMention[];
}

export type CommunityBayId =
  | 'global'
  | 'north-america'
  | 'south-america'
  | 'europe'
  | 'asia'
  | 'oceania'
  | 'africa';

export type CommunityDockId =
  | 'main'
  | 'catch-wall'
  | 'catch-help'
  | 'tackle-talk'
  | 'events';

export interface CommunityMention {
  uid: string;
  username: string;
  displayName: string;
}

export interface CommunityPost {
  id: string;
  author: string;
  authorId: string;
  authorAvatar: string;

  authorReputation?: {
    primaryBadgeKey?: string | null;
    featuredBadgeKeys?: string[];
    reputation?: ReputationSummary;
  };

  image?: string;
  caption: string;
  species: string;
  location: string;
  likes: string[];
  comments: Comment[];
  timestamp: number;

  // Phase 5: community intelligence metadata for shared catch posts.
  catchTimestamp?: number;
  catchDate?: string;
  catchTime?: string;
  weight?: string;
  technique?: string;
  weather?: string;
  waterCondition?: string;
  conditions?: string;
  waterType?: string;
  lureOrBait?: string;
  lureOrBaitType?: 'Lure' | 'Bait' | string;
  rod?: string;
  reel?: string;
  equipmentSummary?: string;
  isShared?: boolean;
  shareToCommunity?: boolean;
  visibility?: 'public' | 'followers' | 'hidden' | string;
  bayId?: CommunityBayId | string;
  bayLabel?: string;
  dockId?: CommunityDockId | string;
  dockLabel?: string;
  roomId?: CommunityDockId | string;
  visibilityScope?: 'global' | 'bay' | 'country' | 'region' | 'nearby' | string;
  sourceCatchId?: string;
  communitySignal?: {
    hasSignal: boolean;
    confidence?: 'low' | 'medium' | 'high';
    species?: string;
    waterName?: string;
    waterType?: string;
    technique?: string;
    lureOrBait?: string;
    timeBucket?: string;
    conditions?: string[];
    createdAtMs?: number;
  };

  // Phase 6: client/back-end optional pattern intelligence metadata.
  // These fields are additive only; older posts without them remain valid.
  patternSignal?: {
    patternKey?: string;
    regionKey?: string;
    hiddenSignalScore?: number;
    labels?: Array<'Matching Conditions' | 'Emerging Pattern' | 'Trending Nearby'>;
    reason?: string;
    createdAtMs?: number;
  };

  likeCount?: number;
  reactionCount?: number;
  commentCount?: number;
  lastCommentAt?: number;
  taggedGear?: {
    rod?: TackleBoxItem;
    reel?: TackleBoxItem;
    lure?: TackleBoxItem;
    bait?: TackleBoxItem;
  };
  status?: 'visible' | 'pending_review' | 'hidden';
  mentions?: CommunityMention[];
}

export interface CommunityProfile {
  username: string;
  avatar: string;
  bio: string;
}

export interface Message {
  id: number;
  sender: string;
  recipient: string;
  text: string;
  timestamp: number;
}

export interface AppUser {
  uid: string;
  name: string;
  displayName?: string;
  username?: string;
  usernameLower?: string;
  handle?: string;
  handleLower?: string;
  avatar: string;
  isAdmin?: boolean;
  isSeller?: boolean;
  xp?: number;
  points: number;
  level: number;
  reputation?: ReputationSummary;
  memberNumber?: number;
  earlyPro?: boolean;
  shells?: number;
  foundingMember?: FoundingMemberRewards;
  entitlements?: UserEntitlements;
}

// ----------------------------------------------------
// WEATHER / AI INSIGHTS
// ----------------------------------------------------
export interface WeatherData {
  current: {
    temp: number | string
    condition: 'Sunny' | 'Cloudy' | 'Rainy' | 'Partly Cloudy' | 'Snow' | 'Stormy'
    feelsLike: number | string
    wind: string
    humidity: string

    moonPhase?: string
    sunrise?: string
    sunset?: string
    nextHighTide?: string
    nextLowTide?: string
  }

  forecast: {
    day: string
    high: number | string
    low: number | string
    condition: 'Sunny' | 'Cloudy' | 'Rainy' | 'Partly Cloudy' | 'Snow' | 'Stormy'
  }[]

  moonPhase?: string
  sunrise?: string
  sunset?: string

  astronomy?: {
    moonPhase?: string
    sunrise?: string
    sunset?: string
  }

  biteWindows?: {
    type?: 'Major' | 'Minor' | string
    label?: string
    window?: string
    time?: string
    value?: string
    reason?: string
    helper?: string
  }[]

  fishBiteScore?: number
  biteScore?: number
  fishIQHints?: (string | { text?: string; label?: string; species?: string })[]
  speciesHints?: (string | { text?: string; label?: string; species?: string })[]

  tides?: {
    mode?: 'inland' | 'coastal' | 'coastal_no_station' | 'coastal_unavailable' | string
    stationId?: string
    stationName?: string
    station?: string
    nextHigh?: string
    nextLow?: string
    message?: string
    events?: {
      type?: 'High' | 'Low' | 'H' | 'L' | string
      time?: string
      value?: string
      ts?: number
    }[]
  }

  marine?: {
    waveHeight?: string
    waveDirection?: string
    wavePeriod?: string
    waterTemp?: string
  }

  fishing?: {
    biteScore?: number
    biteWindows?: any[]
    speciesHints?: any[]
    fishIQHints?: any[]
  }

  [key: string]: any
}


export interface AIInsight {
  score: number;
  summary: string;
  alert: string;
  targetSpecies: string;
}

// ----------------------------------------------------
// MAP / FISHIQ HOTSPOTS
// ----------------------------------------------------

export type Species =
  | 'Bass'
  | 'Trout'
  | 'Catfish'
  | 'Carp'
  | 'Panfish'
  | 'Salmon'
  | 'Pike'
  | 'Walleye'
  | 'Striped Bass'
  | 'Other';

export type TimeOfDay = 'Morning' | 'Afternoon' | 'Evening' | 'Night';

export interface Hotspot {
  id: string;
  name: string;
  species: Species;
  timeOfDay: TimeOfDay;
  position: { lat: number; lng: number };
  tactic: string;
  funFact: string;
  sourceTitle?: string;
  sourceURL?: string;
  waterName?: string;
  waterKind?: 'water' | 'reservoir' | 'waterway' | string;
  waterType?: FishHabitatType | string;
  waterTruthConfidence?:
    | 'confirmed-named-freshwater'
    | 'confirmed-named-coastal'
    | 'confirmed-named-water-feature'
    | 'nearby-generic-water-feature'
    | 'confirmed-water-feature'
    | 'nearby-water-feature'
    | string;
  snapDistanceMeters?: number;
  fishIQSpeciesData?: FishIQHotspotSpeciesData | null;
  fishIQAreaSpeciesData?: FishIQHotspotSpeciesData[] | FishIQSpeciesPrediction[] | null;
  fishIQAreaRead?: FishIQAreaRead | null;
}

// ----------------------------------------------------
// FishIQ / knowledge graph
// ----------------------------------------------------

export type FishIQTab = 'species' | 'regions' | 'saved';

export type FishIQWaterType = 'freshwater' | 'saltwater' | 'brackish' | 'unknown';

export interface FishIQSpeciesPrediction {
  name: string;
  emoji: string;
  confidence: number;
  movement?: string;
  bestWindow?: string;
  /**
   * Internal/expandable detail. Keep compact map cards clean by default.
   */
  reason?: string;
}

export interface FishIQWaterProfile {
  lat: number;
  lng: number;
  waterType: FishIQWaterType;
  waterName?: string | null;
  waterKind?: string | null;
  regionHint?: string | null;
  season?: string | null;
  timeOfDay?: string | null;
  source?: string | null;
  contextClass?: string | null;
  contextReason?: string | null;
  generatedAt: number;
}

export interface FishIQAreaRead {
  lat: number;
  lng: number;
  waterType: FishIQWaterType;
  waterName?: string | null;
  waterKind?: string | null;
  regionHint?: string | null;
  species: FishIQSpeciesPrediction[];
  structureTags: string[];
  movementTags: string[];
  bestWindow: string;
  confidence: number;
  confidenceLabel?: 'Strong local match' | 'Likely match' | 'Possible match' | 'Limited local data';
  evidence?: {
    buckets: Array<{ key: string; label: string; strength: string; score: number; details: string[] }>;
    localSignalRows: number;
    localEvents7d: number;
    localUniqueUsers7d: number;
    localFreshness: 'recent' | 'today' | 'older' | 'none';
    hasPersonalHistory: boolean;
    hasConfirmedWater: boolean;
    fallbackOnly: boolean;
    score: number;
    label: 'Strong local match' | 'Likely match' | 'Possible match' | 'Limited local data';
    why: string[];
  };
  whyThisRead?: string[];
  livePulse?: {
    activityTags: string[];
    signalCount: number;
    eventCount7d: number;
    uniqueUsers7d: number;
    freshness: 'recent' | 'today' | 'older' | 'none';
    label: string;
  };
  generatedAt: number;
}

export type FishCategory = 'freshwater' | 'saltwater' | 'both';

export type AquaticKind =
  | 'finfish'
  | 'crustacean'
  | 'mollusk'
  | 'cephalopod'
  | 'echinoderm'
  | 'plant';

export type AquaticWaterRealm = 'freshwater' | 'saltwater' | 'brackish' | 'anadromous';

export type AquaticHarvestMode =
  | 'rod_and_reel'
  | 'trap'
  | 'net'
  | 'dig'
  | 'dive'
  | 'rake'
  | 'commercial_only'
  | 'observe_only';

export type FishHabitatType =
  | 'lake'
  | 'river'
  | 'reservoir'
  | 'pond'
  | 'creek'
  | 'inshore'
  | 'estuary'
  | 'bay'
  | 'ocean'
  | 'canal'
  | 'marsh'
  | 'reef'
  | 'backwater'
  | 'other';

export type FishSeason = 'spring' | 'summer' | 'fall' | 'winter';

export interface FishTempRange {
  idealMinC: number;
  idealMaxC: number;
  absoluteMinC: number;
  absoluteMaxC: number;
}

export interface FishActivitySeasonNote {
  season: FishSeason;
  activity: 'low' | 'medium' | 'high';
  notes: string;
}

export interface FishBaitRecommendation {
  category: 'live' | 'softPlastic' | 'hardBait' | 'jig' | 'fly' | 'other';
  name: string;
  examples?: string[];
  bestWhen?: string;
}

export interface FishTechniqueTip {
  name: string;
  description?: string;
  bestUsedWhen?: string;
}

export type FishIQActiveTime = 'dawn' | 'day' | 'dusk' | 'night' | string;

export interface FishIQDepthPreference {
  shallow?: string;
  mid?: string;
  deep?: string;
  notes?: string;
}

export interface FishIQImageMeta {
  imageUrl?: string;
  imageCredit?: string;
  imageLicense?: string;
  wikiTitle?: string;
  wikidataId?: string;
}

export interface FishIQHotspotSpeciesData {
  id: string;
  slug?: string;
  commonName?: string;
  name: string;
  scientificName?: string;
  summary?: string;
  description?: string;
  habitats?: FishHabitatType[];
  regions?: string[];
  beginnerTips?: string[];
  advancedTips?: string[];
  behaviorTags?: string[];
  structureTags?: string[];
  depthPreference?: string | FishIQDepthPreference;
  activeTimes?: FishIQActiveTime[];
  feedingProfile?: {
    primaryPrey?: string[];
    [key: string]: any;
  };
  tactics?: {
    bestBaits?: string[];
    bestTechniques?: string[];
    bestSeasons?: string[];
    bestTimesOfDay?: string[];
    [key: string]: any;
  };
  bestBaits?: string[];
  bestTechniques?: string[];
  bestSeasons?: string[];
  bestTimesOfDay?: string[];
  waterName?: string;
  waterKind?: string;
  waterType?: FishHabitatType | string;
  waterMatchLabel?: string;
  notes?: string;
  [key: string]: any;
}

export interface FishStructurePreference {
  structure: string;
  notes?: string;
}

export interface FishWeatherTip {
  situation: string;
  advice: string;
}

export interface FishTimeOfDayTip {
  period: 'dawn' | 'day' | 'dusk' | 'night';
  advice: string;
}

export interface FishSpawnInfo {
  season: FishSeason;
  waterTempC: { min: number; max: number };
  notes?: string;
}

export interface FishIQEditorialSource {
  title: string;
  url?: string;
  publisher?: string;
  sourceType?: 'official' | 'editorial' | 'imported' | 'community-derived';
}

export type FishIQPublicSourceProvider =
  | 'roddo_editorial'
  | 'gbif'
  | 'noaa'
  | 'state_agency'
  | 'usgs'
  | 'nps'
  | 'fws'
  | 'other_public';

export type FishIQPublicSourceKind =
  | 'editorial_seed'
  | 'species_occurrence'
  | 'species_profile'
  | 'regulation'
  | 'habitat'
  | 'waterbody'
  | 'weather_oceanic';

export type FishIQPublicSourceRegistryStatus = 'active' | 'ready' | 'planned' | 'disabled';

export interface FishIQPublicSourceRef {
  id: string;
  provider: FishIQPublicSourceProvider;
  kind: FishIQPublicSourceKind;
  title: string;
  publisher: string;
  url?: string;
  jurisdiction?: string;
  speciesScientificName?: string;
  speciesCommonName?: string;
  accessedAt?: string;
  registryStatus?: FishIQPublicSourceRegistryStatus;
  notes?: string;
}

export interface FishIQTactics {
  bestBaits?: Array<FishBaitRecommendation | string>;
  bestTechniques?: Array<FishTechniqueTip | string>;
  bestSeasons?: FishSeason[] | string[];
  bestTimesOfDay?: Array<'dawn' | 'day' | 'dusk' | 'night' | string>;
}


export interface FishIQSpeciesKnowledge {
  version?: string;
  overview?: string;
  habitatFact?: string;
  feedingFact?: string;
  behaviorFact?: string;
  structureFact?: string;
  depthFact?: string;
  temperatureFact?: string;
  seasonalMovementFact?: string;
  spawningFact?: string;
  reproductionFact?: string;
  seasonalFact?: string;
  timingFact?: string;
  tacticFact?: string;
  fieldNotes?: string[];
  forage?: string[];
  habitatTags?: string[];
  structureTags?: string[];
  behaviorTags?: string[];
  seasonTags?: string[];
  activityWindows?: string[];
  sourceRefs?: string[];
}

export interface FishIQSpecies extends FishIQImageMeta {
  id: string;
  slug?: string;

  commonName: string;
  scientificName?: string;
  aliases?: string[];
  family?: string;
  category?: FishCategory;
  aquaticKind?: AquaticKind;
  waterRealms?: AquaticWaterRealm[];
  harvestModes?: AquaticHarvestMode[];
  edibleCommonly?: boolean;
  fishiqEligible?: boolean;
  caution?: string;

  habitats?: FishHabitatType[];
  regions?: string[];

  summary?: string;
  keyTraits?: string[];
  behaviorTags?: string[];
  structureTags?: string[];
  activeTimes?: FishIQActiveTime[];
  typicalSize?: string;

  tempRange?: FishTempRange;
  depthPreference?: string | FishIQDepthPreference;

  depthPatterns?: {
    shallow?: string;
    mid?: string;
    deep?: string;
  };

  feedingProfile?: {
    primaryPrey?: string[];
    prey?: string[];
    notes?: string;
  };

  spawn?: FishSpawnInfo;
  activityBySeason?: FishActivitySeasonNote[];

  bestBaits?: Array<FishBaitRecommendation | string>;
  bestTechniques?: Array<FishTechniqueTip | string>;
  bestSeasons?: FishSeason[] | string[];
  bestTimesOfDay?: Array<'dawn' | 'day' | 'dusk' | 'night' | string>;

  tactics?: FishIQTactics;
  speciesKnowledge?: FishIQSpeciesKnowledge;

  structurePreferences?: FishStructurePreference[];
  weatherTips?: FishWeatherTip[];
  timeOfDayTips?: FishTimeOfDayTip[];

  beginnerTips?: string[];
  advancedTips?: string[];

  seoSourceKey?: string;
  confidence?: 'editorial' | 'community' | 'derived';
  editorialSources?: FishIQEditorialSource[];
  publicSourceRefs?: string[];
  sourceRegistryVersion?: 'v1' | string;

  createdAt?: number;
  updatedAt?: number;

  [key: string]: any;
}

export interface FishIQTechnique {
  id: string;
  title: string;
  category: 'method' | 'platform' | 'seasonal';

  summary: string;
  whenToUse?: string;
  gearNeeded?: string[];
  bestSpecies?: string[];
  bestRegions?: string[];

  steps?: string[];
  commonMistakes?: string[];
  proTips?: string[];

  level?: 'beginner' | 'intermediate' | 'advanced';
  seoSourceKey?: string;
  createdAt?: number;
  updatedAt?: number;
}

export interface FishIQRegion {
  id: string;
  name: string;

  country?: string;
  stateOrProvince?: string;
  summary?: string;

  waterbodies?: Array<{
    id: string;
    name: string;
    type:
      | 'lake'
      | 'river'
      | 'reservoir'
      | 'pond'
      | 'creek'
      | 'inshore'
      | 'estuary'
      | 'bay'
      | 'ocean'
      | 'canal'
      | 'marsh'
      | 'other';
  }>;

  title?: string;
  scope?: 'country' | 'state' | 'waterbody' | 'checklist';
  topSpecies?: string[];
  popularTechniques?: string[];
  regulationsTips?: string[];
  seasonalPatterns?: string[];
  travelTips?: string[];
  seoSourceKey?: string;
  createdAt?: number;
  updatedAt?: number;
}

export interface FishIQFeatureGuide {
  id: string;
  title: string;
  appFeatureKey?: string;

  summary: string;
  problemWeSolve?: string;
  howRoddoHelps?: string[];
  comparisonAngles?: string[];
  tipsForUsingRoddo?: string[];

  seoSourceKey?: string;
  createdAt?: number;
  updatedAt?: number;
}

export interface FishIQData {
  scientificName: string;
  description: string;
  habitat: string;
  diet: string;
  bestLuresAndBait: string[];
  fishingTips: string[];
  conservationStatus: string;
  regulationsSummary: string;
  sources: Array<{ title: string; uri: string }>;
}

export interface FishIQSpeciesSeed extends FishIQImageMeta {
  id: string;
  slug?: string;

  commonName: string;
  scientificName?: string;
  aliases?: string[];
  family?: string;
  category?: FishCategory;
  aquaticKind?: AquaticKind;
  waterRealms?: AquaticWaterRealm[];
  harvestModes?: AquaticHarvestMode[];
  edibleCommonly?: boolean;
  fishiqEligible?: boolean;
  caution?: string;

  habitats?: FishHabitatType[] | string[];
  regions?: string[];

  summary?: string;
  keyTraits?: string[];
  behaviorTags?: string[];
  structureTags?: string[];
  tempRange?: FishTempRange;
  depthPreference?: string | FishIQDepthPreference;

  depthPatterns?: {
    shallow?: string;
    mid?: string;
    deep?: string;
  };
  activeTimes?: FishIQActiveTime[];
  typicalSize?: string;

  feedingProfile?: {
    primaryPrey?: string[];
    prey?: string[];
    notes?: string;
  };

  spawn?: FishSpawnInfo;
  activityBySeason?: FishActivitySeasonNote[];

  bestBaits?: Array<FishBaitRecommendation | string>;
  bestTechniques?: Array<FishTechniqueTip | string>;
  bestSeasons?: FishSeason[] | string[];
  bestTimesOfDay?: Array<'dawn' | 'day' | 'dusk' | 'night' | string>;

  tactics?: FishIQTactics;

  beginnerTips?: string[];
  advancedTips?: string[];

  seoSourceKey?: string;
  editorialSources?: FishIQEditorialSource[];
  publicSourceRefs?: string[];
  sourceRegistryVersion?: 'v1' | string;
  confidence?: 'editorial' | 'community' | 'derived';
  speciesKnowledge?: FishIQSpeciesKnowledge;
  createdAt?: number;
  updatedAt?: number;
}

// ----------------------------------------------------
// MAP SPOTS / EVENTS / DEVICES
// ----------------------------------------------------

export interface Rating {
  value: number;
  timestamp: number;
  userId: string;
}

export type SavedSpotKind = 'favorite' | 'fish' | 'food' | 'launch' | 'sunset';

export interface SavedSpotProfileRecord {
  id: string;
  name: string;
  kind: SavedSpotKind;
  lat: number;
  lng: number;
  createdAt: number;
  clientCreatedAt?: number;
  note?: string;
}

export interface UserSpot {
  id: string;

  lat?: number;
  lon?: number;

  position?: {
    lat: number;
    lon: number;
    [key: string]: any;
  };

  name?: string;
  description?: string;

  createdAt?: number;
  createdBy: string;

  creatorId?: string;
  creatorName?: string;
  creatorAvatar?: string;
  isPublic?: boolean;
  isFavorite?: boolean;
  [key: string]: any;
}

export interface CommunityEvent {
  id: string;
  name: string;
  position: { lat: number; lng: number };
  radiusKm: number;
  date: string;
}

export interface RestrictedZone {
  id: string;
  name: string;
  position: { lat: number; lng: number };
  radiusMeters: number;
  reason: string;
}

export interface Device {
  id: string;
  name: string;
  category: 'Sonar' | 'Camera' | 'Drone';
}

// ----------------------------------------------------
// TRANSCRIPTS / AI HELPERS
// ----------------------------------------------------

export interface TranscriptEntry {
  author: 'user' | 'ai';
  text: string;
  isFinal?: boolean;
}

export interface SpeciesInsight {
  speciesName: string;
  bestLures: string[];
  bestTimes: string[];
  habitatInfo: string;
  funFact: string;
}

// ----------------------------------------------------
// CHALLENGES / TOURNAMENTS
// ----------------------------------------------------

export interface Challenge {
  id: string;
  title: string;
  description: string;

  points: number;
  goal: number;
  metric: string;
  type: string;

  rewardXP: number;
}

export type TournamentStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'registration_open'
  | 'active'
  | 'submissions_closed'
  | 'under_review'
  | 'provisional_results'
  | 'finalized'
  | 'changes_requested'
  | 'rejected'
  | 'cancelled'
  | 'archived'
  | 'upcoming'
  | 'live'
  | 'completed';

export type TournamentVisibility = 'private' | 'crew' | 'public';
export type TournamentTier = 'crew' | 'community' | 'roddo_official';
export type TournamentParticipationMode = 'solo' | 'team';
export type TournamentScale =
  | 'friend_group'
  | 'local_crew'
  | 'fishing_club'
  | 'boat_crew'
  | 'city'
  | 'province'
  | 'island'
  | 'country'
  | 'open_global';
export type TournamentCompetitionFormat =
  | 'biggest_fish'
  | 'longest_fish'
  | 'heaviest_fish'
  | 'most_fish'
  | 'total_length'
  | 'total_weight'
  | 'species_count'
  | 'custom_points';
export type TournamentScoringMode =
  | 'longest'
  | 'heaviest'
  | 'best_3'
  | 'best_5'
  | 'most_catches'
  | 'species_count'
  | 'total_length'
  | 'checklist'
  | 'team_total'
  | 'roddo_points';
export type TournamentPrizeDistribution =
  | 'winner_take_all'
  | 'top_three'
  | 'top_five'
  | 'team_split';
export type TournamentMatchmakingMode =
  | 'open'
  | 'similar_rating'
  | 'rating_range'
  | 'rating_divisions'
  | 'teams_balanced_by_rating';
export type TournamentVerificationLevel =
  | 'standard'
  | 'verified_equipment'
  | 'professional';
export type TournamentCaptureMode =
  | 'live_photos'
  | 'continuous_video'
  | 'photos_and_video';

export type TournamentSubmissionCapturePolicy =
  | 'live_only'
  | 'live_or_upload';

export type TournamentCaptureSource =
  | 'live_camera'
  | 'uploaded_photo';


export type TournamentParticipantStatus =
  | 'pending'
  | 'approved'
  | 'withdrawn'
  | 'removed'
  | 'disqualified';

export type TournamentParticipantRole =
  | 'participant'
  | 'team_captain';

export interface TournamentParticipant {
  id: string;
  tournamentId: string;
  uid: string;
  displayName: string;
  avatarUrl?: string | null;
  role: TournamentParticipantRole;
  status: TournamentParticipantStatus;
  teamId?: string | null;
  teamName?: string | null;
  joinedAt: number;
  updatedAt?: number;
  approvedAt?: number | null;
  approvedBy?: string | null;
  withdrawnAt?: number | null;
  removedAt?: number | null;
  removedBy?: string | null;
  removalReason?: string | null;
  disqualifiedAt?: number | null;
  disqualifiedBy?: string | null;
  disqualificationReason?: string | null;
  reinstatedAt?: number | null;
  reinstatedBy?: string | null;
  reinstatementReason?: string | null;
}

export interface TournamentTeam {
  id: string;
  tournamentId: string;
  name: string;
  captainUid: string;
  memberUids: string[];
  status: 'forming' | 'active' | 'withdrawn' | 'removed';
  createdAt: number;
  updatedAt?: number;
}

export type TournamentSubmissionStatus =
  | 'submitted'
  | 'accepted'
  | 'rejected'
  | 'withdrawn';

export interface TournamentLiveContext {
  tournamentId: string;
  tournamentName: string;
  tournamentCode?: string | null;
  scoringMode?: TournamentScoringMode | string;
  measurementUnit?: 'in' | 'cm' | 'lb' | 'kg' | 'points';
  locationVerification?: 'none' | 'approximate' | 'required';
  submissionCapturePolicy?: TournamentSubmissionCapturePolicy;
  eligibleSpecies?: string[];
  minQualifyingSize?: number;
  catchLimit?: number;
  lengthBoardRequired?: boolean;
  scaleRequired?: boolean;
  tournamentCodeRequired?: boolean;
}

export interface TournamentSubmission {
  id: string;
  tournamentId: string;
  catchId: string;
  participantUid: string;
  participantName: string;
  participantAvatarUrl?: string | null;
  teamId?: string | null;
  teamName?: string | null;
  species: string;
  photoUrls: string[];
  capturedAt: number;
  diaryCreatedAt?: number;
  location?: string | null;
  coords?: {
    lat: number;
    lon: number;
  } | null;
  measurementValue?: number | null;
  measurementUnit?: 'in' | 'cm' | 'lb' | 'kg' | 'points' | null;
  scoringMode?: TournamentScoringMode | string;
  tournamentCode?: string | null;
  captureSource?: TournamentCaptureSource;
  verificationSnapshot?: {
    captureMode?: TournamentCaptureMode | string;
    submissionCapturePolicy?: TournamentSubmissionCapturePolicy | string;
    captureSource?: TournamentCaptureSource | string;
    lengthBoardRequired?: boolean;
    scaleRequired?: boolean;
    tournamentCodeRequired?: boolean;
    releaseVideoRequired?: boolean;
    locationVerification?: 'none' | 'approximate' | 'required' | string;
  };
  status: TournamentSubmissionStatus;
  submittedAt: number;
  updatedAt?: number;
  reviewedAt?: number | null;
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  reviewNotes?: string | null;
  withdrawnAt?: number | null;
}

export interface TournamentLeaderboardEntry {
  id: string;
  tournamentId: string;
  competitorType: 'participant' | 'team';
  competitorId: string;
  participantUid?: string | null;
  participantName?: string | null;
  teamId?: string | null;
  teamName?: string | null;
  placement: number;
  score: number;
  scoreUnit: 'in' | 'cm' | 'lb' | 'kg' | 'points' | 'catches' | 'species';
  acceptedCatchCount: number;
  speciesCount: number;
  bestCatchValue?: number | null;
  qualifyingSubmissionIds: string[];
  scoringMode: TournamentScoringMode;
  generatedAt: number;
}

export interface TournamentShellPayoutRecipient {
  userId: string;
  userName: string;
  placement: number;
  shellsAwarded: number;
  competitorId?: string;
}

export interface TournamentWinner {
  userId: string;
  userName: string;
  placement: number;
  score: number;
  shellsAwarded?: number;
  prize?: number;
  reasoning?: string;
}

export interface Tournament {
  id: string;
  schemaVersion?: number;
  name: string;
  description?: string;
  directorUid?: string;
  directorName?: string;
  tier?: TournamentTier;
  participationMode?: TournamentParticipationMode;
  visibility?: TournamentVisibility;
  tournamentScale?: 'friend_group' | 'crew' | 'local' | 'regional' | 'national' | 'global';
groupName?: string;
  status?: TournamentStatus;
  approvalStatus?:
    | 'not_submitted'
    | 'pending'
    | 'approved'
    | 'changes_requested'
    | 'rejected';
  submittedAt?: number;
  resubmittedAt?: number | null;
  reviewedAt?: number;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewNotes?: string;
  previousStatus?: TournamentStatus | string;
  approvedAt?: number;
  rejectedAt?: number;
  changesRequestedAt?: number;
  reopenedAt?: number;
  cancelledAt?: number;
  cancelledBy?: string;
  cancellationReason?: string;
  archivedAt?: number;
  archivedBy?: string;
  archiveReason?: string;
  lifecycleUpdatedAt?: number;
  lifecycleUpdatedBy?: string;
  locationLabel?: string;
  locationScope?: 'worldwide' | 'country' | 'region' | 'water' | 'custom';
  /**
   * General/public tournament map pin derived from the public location label.
   * This is not a contestant catch coordinate or private verification location.
   */
  mapLocation?: {
    lat: number;
    lng: number;
    source?: 'location_label_geocode' | 'manual' | string;
    label?: string;
  } | null;
  startDate: number;
  endDate?: number;
  registrationOpensAt?: number;
  registrationClosesAt?: number;
  openRegistrationOnApproval?: boolean;
  timezone?: string;
  maxParticipants?: number;
  participantCount?: number;
  pendingParticipantCount?: number;
  approvedParticipantCount?: number;
  registrationOpenedAt?: number | null;
  registrationClosedAt?: number | null;
  registrationUpdatedBy?: string | null;
  registrationDecision?: {
    registrationRolloutEnabled: boolean;
    openRegistrationOnApproval: boolean;
    scheduledWindowHasOpened: boolean;
    registrationOpensAt: number;
    registrationClosesAt: number;
    evaluatedAt: number;
    opened: boolean;
    reason:
      | 'rollout_disabled'
      | 'registration_deadline_passed'
      | 'waiting_for_scheduled_open'
      | 'opened';
  } | null;
  registrationConfigSnapshot?: {
    registrationEnabled: boolean;
    evaluatedAt: number;
  } | null;
  moderatorCount?: number;
  teamSize?: number;
  crewOnly?: boolean;
  participantApprovalRequired?: boolean;
  entryFeeShells?: number;
  verifiedSubmissionsEnabled?: boolean;
  shellPrizePool?: number;
  shellGrossEntries?: number;
  totalRefundedShells?: number;
  prizeDistribution?: TournamentPrizeDistribution;
  competitionFormat?: TournamentCompetitionFormat;
  scoringMode?: TournamentScoringMode;
  matchmakingMode?: TournamentMatchmakingMode;
  eligibleSpecies?: string[];
  catchLimit?: number;
  minQualifyingSize?: number;
  measurementUnit?: 'in' | 'cm' | 'lb' | 'kg' | 'points';
  minRating?: number;
  maxRating?: number;
  verificationLevel?: TournamentVerificationLevel;
  captureMode?: TournamentCaptureMode;
  catchDisposition?: 'release' | 'harvest' | 'either';
  lengthBoardRequired?: boolean;
  scaleRequired?: boolean;
  tournamentCodeRequired?: boolean;
  tournamentCode?: string | null;
  releaseVideoRequired?: boolean;
  locationVerification?: 'none' | 'approximate' | 'required';
  rulesText?: string;
  allowedMethods?: string[];
  disallowedMethods?: string[];
  disqualificationPolicy?: string;
  disputeWindowHours?: number;
  disputeWindowOpenedAt?: number | null;
  disputeWindowEndsAt?: number | null;
  disputeStatus?: 'open' | 'closed';
  disputeUpdatedAt?: number | null;
  disputeUpdatedBy?: string | null;
  disputeClosedAt?: number | null;
  disputeClosedBy?: string | null;
  disqualifiedParticipantCount?: number;
  rulesLockedAt?: number | null;
  createdAt?: number;
  updatedAt?: number;
  createdBy?: string;
  legacy?: boolean;
  location?: string;
  rules?: string;
  prizePool?: number | string;
  participants?: any[];
  catches?: any[];
  winners?: TournamentWinner[];
  leaderboardGeneratedAt?: number | null;
  resultsFinalizedAt?: number | null;
  resultsFinalizedBy?: string | null;
  unresolvedSubmissionCount?: number;
  payoutStatus?: 'not_applicable' | 'locked' | 'pending' | 'paid' | 'refund_pending' | 'refunded';
  refundStatus?: 'not_required' | 'pending' | 'refunded';
  refundedEntryCount?: number;
  refundedShells?: number;
  refundsCompletedAt?: number;
  payoutDistribution?: TournamentPrizeDistribution | string;
  payoutRecipients?: TournamentShellPayoutRecipient[];
  totalPaidShells?: number;
  payoutsCompletedAt?: number;
  payoutsCompletedBy?: string;
}

export interface TournamentPlatformConfig {
  schemaVersion: 1;
  adminPreviewEnabled: boolean;
  publicAccessEnabled: boolean;
  creationEnabled: boolean;
  shellEntryEnabled: boolean;
  submissionEnabled: boolean;
  registrationEnabled: boolean;
  updatedAt?: number;
  updatedBy?: string;
}

// ----------------------------------------------------
// CREW / RELATIONSHIPS / PRESENCE
// ----------------------------------------------------

export type SocialRelationshipStatus =
  | 'none'
  | 'pending_sent'
  | 'pending_received'
  | 'friends'
  | 'accepted'
  | 'declined'
  | 'removed'
  | 'blocked'
  | 'blocked_by_me'
  | 'blocked_me';

export interface UserRelationship {
  id: string;
  participantIds: string[];
  requesterId: string;
  recipientId: string;
  status: 'pending' | 'accepted' | 'declined' | 'removed' | 'blocked' | string;
  createdAt?: number;
  updatedAt?: number;
  acceptedAt?: number;
  declinedAt?: number;
  removedAt?: number;
  removedBy?: string;
  blockedAt?: number;
  blockedBy?: string;
}

export interface CrewPresence {
  uid: string;
  displayName?: string;
  avatarUrl?: string;
  isOnline?: boolean;
  /** Alias written for easier Firestore inspection / future clients. */
  online?: boolean;
  locationSharing?: 'off' | 'crew';
  /** Local millisecond heartbeat used to expire stale online markers. */
  lastSeenAt?: number;
  heartbeatAt?: number;
  updatedAt?: number;
  approxLocation?: {
    lat: number;
    lng: number;
  };
}
